import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  BarChart3,
  Clock,
  Zap,
  ChevronDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { openAgent } from "../utils/openAgent";

const caseStudiesList = [
  {
    id: "retail-chain",
    tag: "RETAIL | CUSTOMER EXPERIENCE",
    title: "Leading Retail Chain",
    description:
      "Personalized customer experiences and inventory optimization using AI and analytics.",
    image: "/images/case-studies/retail-chain.jpg",
  },
  {
    id: "skipper-limited",
    tag: "MANUFACTURING | OPERATIONS",
    title: "Skipper Limited",
    description:
      "Digitizing manufacturing processes across galvanizing and tower operations.",
    image: "/images/case-studies/skipper-limited.jpg",
  },
  {
    id: "aptia-uk",
    tag: "FINANCIAL SERVICES | GROWTH",
    title: "Aptia UK",
    description:
      "Driving customer acquisition and improving digital user journeys through data and AI.",
    image: "/images/case-studies/aptia-uk.jpg",
  },
  {
    id: "logistics-provider",
    tag: "SUPPLY CHAIN | OPERATIONS",
    title: "Global Logistics Provider",
    description:
      "End-to-end visibility and demand forecasting for a more resilient supply chain.",
    image: "/images/case-studies/logistics-provider.jpg",
  },
  {
    id: "healthcare-network",
    tag: "HEALTHCARE | OPERATIONS",
    title: "Healthcare Network",
    description:
      "Streamlining workflows and improving patient experience with digital solutions.",
    image: "/images/case-studies/healthcare-network.jpg",
  },
  {
    id: "renewable-energy",
    tag: "ENERGY | SUSTAINABILITY",
    title: "Renewable Energy Enterprise",
    description:
      "Data-driven insights to optimize renewable asset performance and support growth.",
    image: "/images/case-studies/renewable-energy.jpg",
  },
  {
    id: "financial-firm",
    tag: "BANKING | RISK & COMPLIANCE",
    title: "Financial Services Firm",
    description:
      "AI-enabled risk analysis and compliance monitoring for greater confidence.",
    image: "/images/case-studies/financial-services.jpg",
  },
  {
    id: "government-agency",
    tag: "PUBLIC SECTOR | DIGITAL SERVICES",
    title: "Government Agency",
    description:
      "Modernizing citizen services and improving operational efficiency with technology.",
    image: "/images/case-studies/government-agency.jpg",
  },
];

const CaseStudies = () => {
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState("Latest");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleDiscoverClick = () => {
    openAgent();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="min-h-screen w-full text-white flex flex-col justify-between selection:bg-[#2099EC]/30 selection:text-white"
      style={{
        backgroundColor: "rgba(2, 17, 28, 1)",
      }}
    >
      <Navbar />

      {/* HERO SECTION */}
      <main className="relative w-full flex-1 flex items-center overflow-hidden mt-[76px] sm:mt-[80px] py-16 lg:py-24">
        {/* Top-left ambient glow */}
        <div
          className="pointer-events-none absolute left-0 top-0 w-[580px] h-[320px] opacity-70 blur-[80px]"
          style={{
            background:
              "radial-gradient(ellipse at 15% 0%, rgba(23, 56, 98, 1) 0%, rgba(23, 56, 98, 0.45) 50%, rgba(2, 17, 28, 0) 80%)",
          }}
        />

        <div className="relative mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
            {/* Left Column */}
            <div className="flex flex-col items-start gap-5 lg:col-span-6 z-10">
              {/* Eyebrow Breadcrumb Tag */}
              <p className="text-[11px] font-bold uppercase tracking-[2.5px] text-[#808A95]">
                COGNITIVE AI &gt; CASE STUDIES
              </p>

              {/* Headline */}
              <h1 className="text-[38px] sm:text-[46px] lg:text-[54px] xl:text-[60px] font-bold leading-[1.08] tracking-tight text-[#F5F6F6]">
                Real challenges<br />
                <span className="text-[#00D2EE]">through solutions.</span>
              </h1>

              {/* Paragraph */}
              <p className="max-w-[500px] text-[15px] sm:text-[16px] leading-[26px] text-[#94A3B8] mt-1">
                Explore how we help businesses across industries transform
                processes, improve experiences and create new opportunities
                with technology and AI.
              </p>

              {/* Glowing CTA Button */}
              <div className="pt-3">
                <button
                  type="button"
                  onClick={handleDiscoverClick}
                  className="group flex items-center gap-2.5 rounded-full bg-white px-7 py-3.5 sm:px-8 sm:py-3.5 text-[14.5px] sm:text-[15px] font-semibold text-[#0B132B] shadow-[0_10px_24px_-2px_rgba(29,78,216,0.55),0_4px_10px_rgba(37,99,235,0.35)] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_12px_28px_-2px_rgba(29,78,216,0.65),0_4px_12px_rgba(37,99,235,0.4)] active:scale-95 cursor-pointer"
                >
                  <span>Discover Your AI Opportunities</span>
                  <ArrowRight
                    className="h-4 w-4 text-[#0B132B] transition-transform duration-300 group-hover:translate-x-1"
                    strokeWidth={2}
                  />
                </button>
              </div>
            </div>

            {/* Right Column: Hero Graphic Visual with Earth Globe & 5 Floating Industry Cards */}
            <div className="flex w-full items-center justify-center lg:col-span-6 lg:justify-end">
              <div className="relative w-full max-w-[660px] aspect-[884/676] select-none">
                {/* Central Glowing Earth Network PNG */}
                <img
                  src="/images/earth-network.png"
                  alt="Global AI Earth Network"
                  className="absolute inset-0 w-full h-full object-contain pointer-events-none drop-shadow-[0_0_60px_rgba(0,210,238,0.3)] z-10"
                />

                {/* Card 1: Manufacturing Robotics (Top) */}
                <div
                  className="absolute rounded-[12px] sm:rounded-[16px] border-[2px] sm:border-[2.5px] border-white overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.7)] z-20 transition-transform duration-300 hover:scale-105"
                  style={{
                    left: "30.88%",
                    top: "8.43%",
                    width: "17.76%",
                    height: "18.05%",
                  }}
                >
                  <img
                    src="/images/card-robotics.jpg"
                    alt="Manufacturing Automation"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Card 2: Retail Experience (Left - tucked behind Earth's glowing sphere) */}
                <div
                  className="absolute rounded-[12px] sm:rounded-[16px] border-[2px] sm:border-[2.5px] border-white overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.7)] z-[1] transition-transform duration-300 hover:scale-105"
                  style={{
                    left: "7.47%",
                    top: "29.59%",
                    width: "21.38%",
                    height: "21.45%",
                  }}
                >
                  <img
                    src="/images/card-retail.jpg"
                    alt="Retail Experience"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Card 3: Enterprise Tech Team (Bottom-Left) */}
                <div
                  className="absolute rounded-[12px] sm:rounded-[16px] border-[2px] sm:border-[2.5px] border-white overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.7)] z-20 transition-transform duration-300 hover:scale-105"
                  style={{
                    left: "17.42%",
                    top: "65.09%",
                    width: "21.38%",
                    height: "18.64%",
                  }}
                >
                  <img
                    src="/images/card-tech-team.jpg"
                    alt="Tech Collaboration"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Card 4: Corporate Boardroom (Top-Right) */}
                <div
                  className="absolute rounded-[12px] sm:rounded-[16px] border-[2px] sm:border-[2.5px] border-white overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.7)] z-20 transition-transform duration-300 hover:scale-105"
                  style={{
                    left: "65.95%",
                    top: "22.19%",
                    width: "22.17%",
                    height: "22.04%",
                  }}
                >
                  <img
                    src="/images/card-boardroom.jpg"
                    alt="Corporate Strategy"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Card 5: Logistics & Supply Chain Truck (Bottom-Right) */}
                <div
                  className="absolute rounded-[12px] sm:rounded-[16px] border-[2px] sm:border-[2.5px] border-white overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.7)] z-20 transition-transform duration-300 hover:scale-105"
                  style={{
                    left: "59.95%",
                    top: "56.21%",
                    width: "21.49%",
                    height: "17.46%",
                  }}
                >
                  <img
                    src="/images/card-logistics.jpg"
                    alt="Logistics & Supply Chain"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* FEATURED CASE STUDY SECTION */}
      <section className="relative w-full bg-[#F2F8FC] py-10 sm:py-14 text-[#0F172A]">
        <div className="mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
          {/* Featured Case Study Card */}
          <div className="grid grid-cols-1 overflow-hidden rounded-[24px] sm:rounded-[28px] border border-slate-200/80 bg-white shadow-[0_12px_40px_-15px_rgba(15,23,42,0.06)] lg:grid-cols-2">
            {/* Left Column: Factory Floor Photo with Badges */}
            <div className="relative min-h-[340px] sm:min-h-[400px] lg:min-h-[480px] overflow-hidden">
              <img
                src="/images/skipper-factory.jpg"
                alt="Digitizing manufacturing operations at Skipper Limited"
                className="h-full w-full object-cover"
              />

              {/* Top-Left Badge: FEATURED CASE STUDY */}
              <div className="absolute left-5 top-5 z-10">
                <span className="rounded-full bg-[#00D2EE] px-4 py-1.5 text-[11px] sm:text-[11.5px] font-bold uppercase tracking-[1.5px] text-[#033446] shadow-sm">
                  FEATURED CASE STUDY
                </span>
              </div>

              {/* Bottom-Left Pill: SKIPPER LIMITED */}
              <div className="absolute bottom-5 left-5 z-10">
                <span className="rounded-[8px] bg-[#0B132B]/60 px-4 py-2 text-[12px] sm:text-[13px] font-bold uppercase tracking-[2px] text-white backdrop-blur-md border border-white/10 shadow-md">
                  SKIPPER LIMITED
                </span>
              </div>
            </div>

            {/* Right Column: Case Study Details & Stats */}
            <div className="flex flex-col justify-between gap-5 p-6 sm:p-8 lg:p-10 xl:p-12">
              <div className="flex flex-col gap-3.5">
                {/* Category Metadata */}
                <p className="text-[12px] font-bold uppercase tracking-[2px] text-[#0284C7]">
                  MANUFACTURING{" "}
                  <span className="mx-2 font-normal text-slate-300">|</span>{" "}
                  OPERATIONS
                </p>

                {/* Main Headline */}
                <h3 className="text-[24px] sm:text-[28px] lg:text-[32px] xl:text-[34px] font-bold leading-[1.22] tracking-tight text-[#0B132B]">
                  Digitizing manufacturing operations for greater efficiency.
                </h3>

                {/* Body Paragraph */}
                <p className="text-[14px] sm:text-[15px] leading-[24px] sm:leading-[25px] text-[#64748B]">
                  See how Skipper Limited partnered with Simpel to streamline
                  manufacturing processes, improve visibility and enhance
                  operational efficiency across multiple facilities.
                </p>

                {/* 3 Stat Metrics Cards */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-3.5 pt-2">
                  {/* Stat 1 */}
                  <div className="flex flex-col gap-2 rounded-[14px] border border-[#E0F2FE] bg-[#F0F7FF] p-3.5 sm:p-4 transition-all duration-200 hover:border-[#BAE6FD]">
                    <div className="flex h-8 w-8 items-center justify-center rounded-[8px] bg-[#0284C7] text-white shadow-sm">
                      <BarChart3 className="h-4 w-4" />
                    </div>
                    <span className="text-[22px] sm:text-[24px] font-bold leading-none text-[#0B132B]">
                      30%
                    </span>
                    <span className="text-[11px] sm:text-[11.5px] leading-tight text-[#64748B]">
                      Increase in production efficiency
                    </span>
                  </div>

                  {/* Stat 2 */}
                  <div className="flex flex-col gap-2 rounded-[14px] border border-[#E0F2FE] bg-[#F0F7FF] p-3.5 sm:p-4 transition-all duration-200 hover:border-[#BAE6FD]">
                    <div className="flex h-8 w-8 items-center justify-center rounded-[8px] bg-[#0284C7] text-white shadow-sm">
                      <Clock className="h-4 w-4" />
                    </div>
                    <span className="text-[22px] sm:text-[24px] font-bold leading-none text-[#0B132B]">
                      25%
                    </span>
                    <span className="text-[11px] sm:text-[11.5px] leading-tight text-[#64748B]">
                      Reduction in downtime
                    </span>
                  </div>

                  {/* Stat 3 */}
                  <div className="flex flex-col gap-2 rounded-[14px] border border-[#E0F2FE] bg-[#F0F7FF] p-3.5 sm:p-4 transition-all duration-200 hover:border-[#BAE6FD]">
                    <div className="flex h-8 w-8 items-center justify-center rounded-[8px] bg-[#0284C7] text-white shadow-sm">
                      <Zap className="h-4 w-4" />
                    </div>
                    <span className="text-[22px] sm:text-[24px] font-bold leading-none text-[#0B132B]">
                      18%
                    </span>
                    <span className="text-[11px] sm:text-[11.5px] leading-tight text-[#64748B]">
                      Faster decision making
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => navigate("/case-studies/skipper-limited")}
                  className="group flex w-fit items-center gap-2.5 rounded-full bg-gradient-to-r from-[#00D2EE] to-[#2563EB] px-6 sm:px-7 py-3 text-[13.5px] sm:text-[14px] font-semibold text-white shadow-[0_4px_18px_rgba(37,99,235,0.35)] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_6px_22px_rgba(37,99,235,0.45)] active:scale-95 cursor-pointer"
                >
                  <span>Read the Full Case Study</span>
                  <ArrowRight
                    className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                    strokeWidth={2.5}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EXPLORE ALL CASE STUDIES GRID SECTION */}
      <section
        className="relative w-full py-16 sm:py-24 text-[#0F172A]"
        style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
      >
        <div className="mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
          {/* Section Header with Sort Dropdown */}
          <div className="mb-6 sm:mb-8 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex flex-col gap-2">
              <p className="text-[11.5px] sm:text-[12px] font-bold uppercase tracking-[2px] text-[#64748B]">
                ALL CASE STUDIES
              </p>
              <h2 className="text-[30px] sm:text-[36px] lg:text-[40px] font-bold tracking-tight text-[#0B132B]">
                Explore our case studies.
              </h2>
              <p className="text-[14.5px] sm:text-[15.5px] text-[#64748B]">
                Discover how we have helped businesses solve real challenges and create measurable impact.
              </p>
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-2.5 self-start sm:self-auto">
              <span className="text-[13px] font-medium text-[#64748B]">Sort by</span>
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none rounded-[10px] border border-slate-200/90 bg-white py-2 pl-4 pr-9 text-[13.5px] font-medium text-[#0B132B] shadow-sm transition-all focus:border-[#00D2EE] focus:outline-none cursor-pointer"
                >
                  <option value="Latest">Latest</option>
                  <option value="Popular">Most Popular</option>
                  <option value="Featured">Featured</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748B]" />
              </div>
            </div>
          </div>

          {/* Horizontal Divider Line */}
          <div className="w-full border-b border-slate-200/80 mb-10 sm:mb-12" />

          {/* 8 Cards Grid (4 Columns on Desktop, 2 on Tablet, 1 on Mobile) */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {caseStudiesList.map((study) => (
              <div
                key={study.id}
                onClick={() => {
                  if (study.id === "skipper-limited") {
                    navigate("/case-studies/skipper-limited");
                  }
                }}
                className="group relative flex flex-col justify-between overflow-hidden rounded-[20px] border border-slate-200/80 bg-white shadow-[0_4px_20px_-4px_rgba(15,23,42,0.05)] transition-all duration-300 hover:shadow-[0_16px_36px_-8px_rgba(15,23,42,0.1)] cursor-pointer"
              >
                <div className="relative z-10">
                  {/* Thumbnail Image (Completely Static - No Zoom) */}
                  <div className="relative aspect-[16/9] w-full overflow-hidden bg-slate-100">
                    <img
                      src={study.image}
                      alt={study.title}
                      className="h-full w-full object-cover"
                    />
                  </div>

                  {/* Card Content */}
                  <div className="flex flex-col gap-2.5 p-5 sm:p-6">
                    <p className="text-[11px] font-bold uppercase tracking-[1.5px] text-[#0284C7]">
                      {study.tag}
                    </p>
                    <h3 className="text-[17px] sm:text-[18px] font-bold leading-snug text-[#0B132B]">
                      {study.title}
                    </h3>
                    <p className="text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                      {study.description}
                    </p>
                  </div>
                </div>

                {/* Read Case Study Link with Cyan-to-Blue Gradient */}
                <div className="relative z-10 px-5 pb-5 pt-1 sm:px-6 sm:pb-6">
                  <span className="flex w-fit items-center gap-2 text-[13.5px] font-bold">
                    <span className="bg-gradient-to-r from-[#00D2EE] via-[#0284C7] to-[#2563EB] bg-clip-text text-transparent">
                      Read Case Study
                    </span>
                    <ArrowRight
                      className="h-3.5 w-3.5 text-[#0090FF] transition-transform duration-300 group-hover:translate-x-1"
                      strokeWidth={2.5}
                    />
                  </span>
                </div>

                {/* Bottom-Right Corner Blue Aura Glow on Hover */}
                <div
                  className="pointer-events-none absolute inset-0 z-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{
                    background:
                      "radial-gradient(circle at 100% 100%, rgba(193, 216, 252, 0.85) 0%, rgba(210, 230, 252, 0.45) 40%, rgba(255, 255, 255, 0) 72%)",
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LET'S TALK / AI OPPORTUNITIES CTA SECTION */}
      <section
        id="case-studies-cta"
        className="relative flex min-h-[460px] sm:min-h-[500px] lg:min-h-[540px] w-full items-center overflow-hidden bg-[#020B16]"
      >
        {/* Background Graphic Visual */}
        <img
          src="/images/case-studies-cta-bg.png"
          alt="Global Horizon and Network"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover object-right sm:object-center"
        />

        {/* Soft Vignette Overlay for Text Legibility */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#020B16]/85 via-[#020B16]/40 to-transparent sm:via-[#020B16]/20" />

        {/* Content Container Aligned Left */}
        <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 py-16 sm:py-20 lg:px-[70px] lg:py-24">
          <div className="flex max-w-[760px] flex-col items-start gap-5 sm:gap-6">
            {/* Eyebrow Tag */}
            <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-white/70">
              LET'S TALK
            </p>

            {/* Main Headline */}
            <h2 className="text-[28px] sm:text-[36px] md:text-[44px] lg:text-[48px] font-bold leading-[1.15] tracking-tight text-white">
              Enough about what AI can do.<br />
              <span className="text-[#8EDBFB]">
                Let's discover what it can do for your business.
              </span>
            </h2>

            {/* Description Subtitle */}
            <p className="max-w-[540px] text-[14.5px] sm:text-[15.5px] leading-[24px] sm:leading-[26px] text-white/70">
              Tell us about your company or website and we'll show you your
              personalized AI opportunities.
            </p>

            {/* Pill Outline Button */}
            <div className="pt-2">
              <button
                type="button"
                onClick={openAgent}
                className="rounded-full border border-white/80 bg-transparent px-7 py-3 text-[14px] sm:text-[14.5px] font-medium text-white transition-all duration-300 hover:bg-white hover:text-[#0B132B] hover:shadow-[0_0_20px_rgba(255,255,255,0.4)] active:scale-95 cursor-pointer"
              >
                Talk to an AI Strategy Expert
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </motion.div>
  );
};

export default CaseStudies;
